import { h } from 'preact'
import { useState, useEffect, useCallback, useMemo } from 'preact/hooks'
import register from 'preact-custom-element'
import ContentLoader from 'react-content-loader'
import { parseISO, format } from 'date-fns'

const decodeEntities = (string) => {
  // TODO: allow only known html entities
  return { __html: string }
}

const range = (start, end) => {
  const length = end - start
  return Array.from({ length }, (_, i) => start + i)
}

const LoadingCard = () => {
  return (
    <>
      <ContentLoader
        speed={2}
        width="100%"
        height="210px"
        className="mb-20 m:h-[175px] ms:h-[140px] s:h-[115px]"
        backgroundColor="#eee"
        foregroundColor="#ddd">
        <rect
          x="0"
          y="0"
          rx="0"
          ry="0"
          width="210"
          height="210"
          class="ms:w-full"
        />
        <rect
          x="230"
          y="0"
          rx="0"
          ry="0"
          width="100%"
          height="210"
          class="ms:hidden"
        />
      </ContentLoader>
    </>
  )
}

const LoadingCards = ({ total }) => {
  const cardsToShow = range(0, total).map((id) => <LoadingCard key={id} />)
  return cardsToShow
}

const Card = ({ id, link, title, date, image }) => {
  const postTitle = title?.rendered ?? ''
  const dateParsed = parseISO(date)
  const dateFormatted = format(dateParsed, 'EEEE do LLLL yyyy')

  return (
    <>
      <a href={link} title="" class="infinite-list block group">
        <figure class="flex mb-16 overflow-hidden bg-inverse transition-all ease-in-out transform hover:-translate-y-2 hover:shadow">
          {image ? (
            <div class="w-1/5 m:w-1/4 ms:w-1/3">
              <img
                src={image}
                loading="lazy"
                width="250"
                height="250"
                class="h-full object-cover"
              />
            </div>
          ) : (
            ''
          )}

          <figcaption class="flex grow h-full w-4/5 m:w-3/4 ms:w-2/3 p-60 m:p-40 ms:p-20">
            <div class="inline-flex flex-col w-11/12">
              <h3 class="w-1/2 m:w-full text-26 ms:text-18 mb-10 group-hover:text-theme-hover">
                <span dangerouslySetInnerHTML={decodeEntities(postTitle)} />
              </h3>

              <span class="w-full block text-12" datetime={date}>
                {dateFormatted}
              </span>
            </div>

            <div class="inline-flex justify-end items-center w-1/12">
              <svg
                width="20"
                height="17"
                aria-hidden="true"
                class="group-hover:text-theme-hover">
                <use xlinkHref="#icon-arrow" />
              </svg>
            </div>
          </figcaption>
        </figure>
      </a>
    </>
  )
}

const InfiniteList = ({
  postType,
  postsToShow,
  order = 'desc',
  startFrom = 0,
  showLoadMore = false,
}) => {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [endOfPosts, setEndOfPosts] = useState(false)
  const [offset, setOffset] = useState(startFrom)

  const fetchData = async () => {
    setLoading(true)

    const totalWithOffset = posts?.length
      ? parseInt(offset) + parseInt(postsToShow)
      : offset

    const url = `${window.location.origin}/wp-json/wp/v2/${postType}?per_page=${postsToShow}&_field=id,date,title,link,acf&order=${order}&offset=${totalWithOffset}`
    const res = await fetch(url)
    const newPosts = await res.json()

    if (newPosts?.length < postsToShow) {
      setEndOfPosts(true)
    }

    setPosts((prevPosts) => [...prevPosts, ...newPosts])
    setOffset(totalWithOffset)
    setLoading(false)
  }

  useEffect(() => fetchData(), [])

  const handleLoadMore = useCallback(() => fetchData(), [posts, offset])

  const allPosts = useMemo(
    () =>
      posts.map((post) => (
        <li key={post.id}>
          <Card
            id={post.id}
            link={post.link}
            title={post.title}
            date={post.date}
            image={post?.acf?.thumbnail_image?.sizes?.medium_large}
          />
        </li>
      )),
    [posts],
  )

  return (
    <>
      {allPosts?.length ? <ul>{allPosts}</ul> : ''}

      {loading ? <LoadingCards total={postsToShow} /> : ''}

      {showLoadMore && !endOfPosts ? (
        <div class="text-center mt-40">
          <button
            type="button"
            disabled={loading}
            onClick={handleLoadMore}
            class="btn btn--m btn--theme w-full text-center rounded">
            {loading ? 'Loading More...' : 'Load More'}
          </button>
        </div>
      ) : (
        ''
      )}
    </>
  )
}

register(InfiniteList, 'x-infinite-list', [
  'postType',
  'postsToShow',
  'order',
  'startFrom',
  'showLoadMore',
])
